/* eslint-disable @typescript-eslint/naming-convention */
import { useTrackers } from '@ps/web-shared-analytics';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  CasinoSliderButtons,
  ColoredButton,
  GamesCard,
  type Hand,
  useWidgetAnchor
} from '@swf/casino';
import { Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import type { SliderHandles, State as SliderState } from '@swf/components/Slider/Slider';
import Slider from '@swf/components/Slider/Slider';
import { Header } from '@swf/core/enums';
import { select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { getStyle } from '@swf/core/utils/casino';

import styles from './CasinoGameDeckWidget.scss';
import { casinoGameDeckWidgetModel, useCasinoGameDeckWidget } from './model';

const CasinoGameDeckWidget: WidgetFC = () => {
  const { widgetConfig, widgetSelect, widgetDispatch } = useCasinoGameDeckWidget();
  const isMobile = useSelector(select.device.isMobile);
  const containerRef = React.createRef<HTMLDivElement>();
  const [selected, setSelected] = useState(0);
  const sliderRef = React.createRef<SliderHandles>();
  const tabSliderRef = React.createRef<SliderHandles>();
  const [sliderState, setSliderState] = useState<SliderState>({ left: false, right: false });
  const innerRef = useRef<HTMLDivElement>(null);
  const widgetTargetId = useWidgetAnchor();
  const { hands = [], isLoading } = useSelector(widgetSelect.get);
  const { layout, render_info: renderInfo, overlay_hierarchy: overlayHierarchy } = widgetConfig;
  const cards = hands.length ? hands[selected].cards : [];
  const { tabClicked } = useTrackers();

  const onNextButtonClick = (cardIndex: number) => {
    widgetDispatch.loadNextPage({
      handIndex: selected,
      cardIndex
    });
  };

  return !isLoading && cards.length ? (
    <section
      className={styles.container}
      style={getStyle(layout)}
      ref={containerRef}
      id={widgetTargetId}
    >
      <Vary by={[Header.Country, Header.Currency]} />
      <div className={styles.headerButtons}>
        <Slider className={styles.tabSlider} innerClassName={styles.innerSlider} ref={tabSliderRef}>
          {hands.map((hand: Hand, index: number) => (
            <ColoredButton
              key={`${hand.baseColor}T${index}`}
              message={hand.title}
              active={index === selected}
              baseColor={hand.baseColor}
              onClick={() => {
                sliderRef.current?.reset();
                setSelected(index);
                tabClicked({ tabName: hand.title.defaultMessage });
              }}
            />
          ))}
        </Slider>
        {!isMobile && (
          <div className={styles.sliderButtons}>
            <CasinoSliderButtons sliderRef={sliderRef} sliderState={sliderState} />
          </div>
        )}
      </div>
      <div className={styles.cardContainer}>
        <Slider
          innerClassName={styles.innerSlider}
          ref={sliderRef}
          onScroll={setSliderState}
          innerRef={innerRef}
        >
          {cards.map((card, cIndex) => (
            <GamesCard
              key={`${card.baseColor}GC${cIndex}`}
              renderInfo={renderInfo?.length ? renderInfo : undefined}
              overlayHierarchy={overlayHierarchy}
              onNextButtonClick={() => {
                onNextButtonClick(cIndex);
              }}
              {...card}
            />
          ))}
        </Slider>
      </div>
    </section>
  ) : null;
};

CasinoGameDeckWidget.model = casinoGameDeckWidgetModel;

export default CasinoGameDeckWidget;
